import { CircularProgress } from '@mui/material'
import { toast } from 'react-toastify'
import React, { useMemo, useRef, useState, useEffect } from 'react'
import { CustomImageAvatarButton } from 'src/consumer/components/helperComponentsStyles'
import { calculateMinWidthAndHeight } from 'src/consumer/helpers/utilFunctions'
import { postDataWithParams } from 'src/consumer/services/profileService'
import { sleep } from 'src/mentor/helpers/utilityFunctions'
import { FilePond, registerPlugin } from 'react-filepond'
import { apiURL } from 'src/consumer/services'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import FilePondPluginGetFile from 'filepond-plugin-get-file'
import 'filepond-plugin-get-file/dist/filepond-plugin-get-file.min.css'
import FilePondPluginImageEdit from 'filepond-plugin-image-edit'
import 'filepond-plugin-image-edit/dist/filepond-plugin-image-edit.css'
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation'
import FilePondPluginImagePreview from 'filepond-plugin-image-preview'
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css'
import FilePondPluginImageTransform from 'filepond-plugin-image-transform'
import FilePondPluginImageValidateSize from 'filepond-plugin-image-validate-size'
import 'filepond/dist/filepond.min.css'
import { createDokaEditor } from 'src/consumer/helpers/dokaLoader'

registerPlugin(
  FilePondPluginImageExifOrientation,
  FilePondPluginImagePreview,
  FilePondPluginImageEdit,
  FilePondPluginGetFile,
  FilePondPluginFileValidateType,
  FilePondPluginImageTransform,
  FilePondPluginImageValidateSize,
)

export default function PictureUploadButton({
  onChange,
  aspectHeight,
  aspectWidth,
  imageUploadUrl = '/api/media/file/upload',
  fileUploadApiParams = {},
  preferCamera = false,
  onUploadStart,
  onUploadError,
  ...rest
}) {
  const inputRef = useRef(null)
  const [saving, setSaving] = useState(false)
  const [files, setFiles] = useState()
  const [fileIsThere, setFileIsThere] = useState(false)
  const [dokaEditor, setDokaEditor] = useState(null)
  const cssAspectRatio = useMemo(() => {
    if (!aspectHeight && !aspectWidth) {
      return undefined
    }
    return aspectWidth && aspectHeight && aspectWidth / aspectHeight
  }, [aspectHeight, aspectWidth])
  const minDimensions =
    !Boolean(aspectWidth) && !Boolean(aspectHeight)
      ? null
      : calculateMinWidthAndHeight(cssAspectRatio)

  // Load Doka editor asynchronously (non-blocking)
  useEffect(() => {
    let isMounted = true

    let _editor = null

    createDokaEditor({
      cropAspectRatio: !cssAspectRatio ? undefined : 1 / cssAspectRatio,
    }).then((editor) => {
      if (isMounted) {
        _editor = editor
        setDokaEditor(editor)
      } else {
        // Unmounted before Doka loaded — destroy immediately to free WebGL/WASM resources
        try { editor?.destroy?.() } catch (_) {}
      }
    }).catch((error) => {
      console.error('Failed to load Doka editor:', error)
    })

    return () => {
      isMounted = false
      // Destroy Doka editor to release WebGL/WASM context (prevents 2nd instance from being slow)
      if (_editor) {
        try { _editor.destroy?.() } catch (_) {}
        _editor = null
      }
      setDokaEditor(null)
    }
  }, [cssAspectRatio])

  function handleClick() {
    inputRef.current.browse()
  }

  async function processImage(
    fieldName,
    file,
    metadata,
    load,
    error,
    progress,
    abort,
    transfer,
    options,
  ) {
    // fieldName is the name of the input field
    // file is the actual file object to send
    setSaving(true)
    
    // Notify parent that upload has started
    if (onUploadStart) {
      onUploadStart()
    }
    
    const formData = new FormData()
    formData.append('file', file)
    
    try {
      const response = await postDataWithParams(imageUploadUrl, formData, fileUploadApiParams)
      setSaving(false)
      
      if (response?.status === 200) {
        await sleep(1000)
        onChange({
          target: { value: response.data.fileId },
        })
        // Note: Upload end is handled by parent via onChange callback
      } else {
        toast.error('Something went wrong')
        if (onUploadError) {
          onUploadError()
        }
        return 0
      }
    } catch (err) {
      setSaving(false)
      toast.error('Upload failed. Please try again.')
      if (onUploadError) {
        onUploadError()
      }
      return 0
    }
  }

  function handleOnData(formData) {
    const files = formData.getAll('files')?.[1]
    const toReturnFormData = new FormData()
    toReturnFormData.append('file', files)
    return toReturnFormData
  }

  async function handleImageChange(event) {
    setSaving(true)
    const file = event.target.files[0]
    if (file) {
      const formData = new FormData()
      formData.append('file', file)
      const response = await postDataWithParams(
        '/api/media/file/upload',
        formData,
        fileUploadApiParams,
      )
      if (response?.status === 200) {
        await sleep(1000)
        onChange({
          ...event,
          target: { value: response.data.fileId, id: event.target.id },
        })
      } else {
        toast.error('Something went wrong')
        return 0
      }
    }
    event.target.value = ''
    setSaving(false)
  }

  return (
    <div
      className="d-flex flex-column align-items-center justify-content-center"
      style={{ gap: '18px' }}
    >
      <CustomImageAvatarButton
        disabled={saving}
        onClick={handleClick}
        sx={{
          p: 0,
          position: 'relative',
          width: '64px',
          height: '64px',
          fontSize: '32px',
          border: '1px solid #cecece',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: '8px',
        }}
      >
        {saving ? <CircularProgress size={20} sx={{ color: '#cecece' }} /> : '+'}
      </CustomImageAvatarButton>
      <div style={{ display: 'none' }}>
        <FilePond
          allowImageEdit
          allowImageValidateSize
          imageValidateSizeMinWidth={minDimensions?.minWidth || undefined}
          imageValidateSizeMinHeight={minDimensions?.minHeight || undefined}
          imagePreviewHeight={aspectHeight}
          stylePanelLayout="compact"
          // imageEditInstantEdit
          instantUpload={true}
          // files={files}
          // onupdatefiles={setFiles}
          allowMultiple={false}
          server={{
            url: apiURL,
            // process: {
            //   url: '/media/file/upload',
            //   method: 'POST',
            //   // withCredentials: false,
            //   //   headers: {},
            //   //   timeout: 7000,
            //   onload: handleOnLoad,
            //   //   onerror: null,
            //   ondata: handleOnData,
            // },
            process: processImage,
            // load: (source, load, error, progress, abort, headers) => {
            //   var myRequest = new Request(`${mediaURL}${source}`, {
            //     headers: headers,
            //     method: 'GET',
            //     mode: 'cors',
            //   })
            //   if (myRequest) {
            //     fetch(`${mediaURL}${source}`).then(function (response) {
            //       console.log(response)
            //       response.blob().then(function (myBlob) {
            //         load(myBlob)
            //       })
            //     })
            //   }
            // },
          }}
          acceptedFileTypes={['image/*']}
          captureMethod={preferCamera ? 'environment' : undefined}
          // {...(preferCamera && { attributesForInputElement: { capture: 'environment' } })}
          // {...(preferCamera && { attributesForInputElement: { capture: 'environment' } })}
          name="files"
          imageEditInstantEdit
          allowImageTransform
          styleItemPanelAspectRatio={cssAspectRatio ? 1 / cssAspectRatio : null}
          styleButtonProcessItemPosition="bottom"
          stylePanelAspectRatio={cssAspectRatio ? 1 / cssAspectRatio : null}
          labelIdle={'Drop Your Photo here or <span class="filepond--label-action">Browse</span>'}
          ref={inputRef}
          imageEditEditor={dokaEditor}
        />
      </div>
    </div>
  )
}
